import React, { createContext, useContext, useEffect, useState } from 'react';
import axios, { isAxiosError } from 'axios';
import { HttpErrorHandler } from '../../../../utils/ErrorHandler';
import API from '../../../../api';
import { fetchGdEntities } from '../../../../services/tesseramento/gdEntitiesService';

// 1. Creiamo il contesto
const UserContext = createContext(null);

// 2. Hook per consumare il context
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// 3. Provider
export const UserProvider = ({ children, userID }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [canModifyUser, setCanModifyUser] = useState(false);

  const [userInfo, setUserInfo] = useState({});
  // Personal Data
  const [name, setName] = useState();
  const [surname, setSurname] = useState();
  const [aliasName, setAliasName] = useState();
  const [useAlias, setUseAlias] = useState(false);
  const [gender, setGender] = useState();
  const [fiscalCode, setFiscalCode] = useState();
  const [email, setEmail] = useState();
  const [birthDate, setBirthDate] = useState();
  const [birthPlace, setBirthPlace] = useState();
  const [phoneNumber, setPhoneNumber] = useState();
  const [callCode, setCallCode] = useState({});
  const [verifiedUser, setVerifiedUser] = useState();
  const [onlineUser, setOnlineUser] = useState();
  const [lockedUser, setLockedUser] = useState();
  const [userEntity, setUserEntity] = useState({});
  const [userGdEntity, setUserGdEntity] = useState(null);
  const [gdEntityList, setGdEntityList] = useState([]);
  const [isBlacklistedEntity, setIsBlacklistedEntity] = useState(false);
  const [isContactUser, setIsContactUser] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [cards, setCards] = useState([]);
  const [positions, setPositions] = useState([]);
  // Residence
  const [resCountry, setResCountry] = useState({});
  const [resRegion, setResRegion] = useState({});
  const [resProvince, setResProvince] = useState({});
  const [resCity, setResCity] = useState({});
  const [resStreet, setResStreet] = useState('');
  const [resZipCode, setResZipCode] = useState('');
  const [resCityName, setResCityName] = useState(null);
  const [resRegionName, setResRegionName] = useState(null);

  // Domicile
  const [domCountry, setDomCountry] = useState({});
  const [domRegion, setDomRegion] = useState({});
  const [domProvince, setDomProvince] = useState({});
  const [domCity, setDomCity] = useState({});
  const [domStreet, setDomStreet] = useState('');
  const [domZipCode, setDomZipCode] = useState('');
  const [domIsRes, setDomIsRes] = useState();
  const [domCityName, setDomCityName] = useState(null);
  const [domRegionName, setDomRegionName] = useState(null);
  const [allowNews, setAllowNews] = useState(Boolean(userInfo.allow_news));

  // user static data

  const getDisplayableGender = (gender) => {
    let genderName;

    if (gender === 'male') {
      genderName = 'Uomo';
    } else if (gender === 'female') {
      genderName = 'Donna';
    } else if (gender === 'other') {
      genderName = 'Altro';
    } else {
      genderName = '';
    }

    return setGender({ gender: genderName });
  };

  useEffect(() => {
    if (!useAlias) {
      setAliasName(null);
    }
  }, [useAlias]);

  const getUserInfo = () => {
    axios
      .all([API.get(`/users/${userID}`)])
      .then(
        axios.spread((userResponse) => {
          setCanModifyUser(userResponse.data.auth_type === 'W' || userResponse.data.auth_type === 'D');
          setUserInfo(userResponse.data);
          setCards(userResponse.data.cards);
          setUserRoles(userResponse.data.roles);
          setPositions(userResponse.data.positions);

          // Utils
          setDomIsRes(Boolean(userResponse.data.res_equal_home === '1'));
          setDomZipCode(userResponse.data.home_zip_code);
          setDomStreet(userResponse.data.home_street);
          setResZipCode(userResponse.data.res_zip_code);
          setResStreet(userResponse.data.res_street);

          // Personal Data
          setName(userResponse.data.name);
          setSurname(userResponse.data.surname);
          setAliasName(userResponse.data.name_alias);
          if (userResponse.data.name_alias && userResponse.data.name_alias.length > 0) {
            setUseAlias(true);
          }
          getDisplayableGender(userResponse.data.gender);
          setFiscalCode(userResponse.data.fiscal_code);
          setBirthDate(userResponse.data.birth_date);
          setBirthPlace(userResponse.data.place_of_birth);
          setEmail(userResponse.data.email);
          setPhoneNumber(userResponse.data.phone);
          setCallCode(userResponse.data.call_code);
          setVerifiedUser(userResponse.data.verified);
          setOnlineUser(userResponse.data.user_online);
          setLockedUser(userResponse.data.blocked);

          const userEntities = userResponse.data.entities;
          const userMasterID = userResponse.data.entity_id;
          const userMasterEntity = userEntities.find((entity) => entity.id === userMasterID);
          setUserEntity(userMasterEntity);
          // Valore entità GD: preferisci l'oggetto risolto dal BE (affidabile anche se l'entità non è
          // tra le user_entities o è fuori dal filtro residenza); fallback sulle user_entities.
          setUserGdEntity(
            userResponse.data.master_entity_gd ||
              userEntities.find((entity) => entity.id === userResponse.data.entity_master_gd) ||
              null
          );
          setIsBlacklistedEntity(userResponse.data.blacklisted_entity);
          setIsContactUser(userResponse.data.pipeline_stage === 'contact');

          // News preference
          setAllowNews(Boolean(userResponse.data.allow_news));

          // Residence
          setResCountry(userResponse.data.res_country);

          const isRegionEmpty = Object.values(userResponse.data.res_region).every((value) => value === null);

          const resRegion = isRegionEmpty ? null : userResponse.data.res_region;

          setResRegion(resRegion);
          setResProvince(userResponse.data.res_province);
          setResCity(userResponse.data.res_city);
          setResCityName(userResponse.data.res_city_name);
          setResRegionName(userResponse.data.res_region_name);

          // Domicile
          setDomCountry(userResponse.data.home_country);
          setDomRegion(userResponse.data.home_region);
          setDomProvince(userResponse.data.home_province);
          setDomCity(userResponse.data.home_city);
          setDomCityName(userResponse.data.home_city_name);
          setDomRegionName(userResponse.data.home_region_name);

          setIsLoading(false);
        })
      )
      .catch((errors) => {
        setIsLoading(false);

        // userResponse Errors
        if (isAxiosError(errors)) {
          HttpErrorHandler(errors, setErrorMessage);
        }
      });
  };

  // Opzioni entità master GD per il campo anagrafico (sprint 019)
  useEffect(() => {
    fetchGdEntities()
      .then(({ entities }) => setGdEntityList(entities))
      .catch(() => setGdEntityList([]));
  }, []);

  // Risolve il valore corrente dell'entità GD dalle opzioni caricate.
  useEffect(() => {
    if (userInfo?.entity_master_gd && gdEntityList.length > 0) {
      const match = gdEntityList.find((e) => e.id === userInfo.entity_master_gd);
      if (match) setUserGdEntity(match);
    }
  }, [userInfo, gdEntityList]);

  const value = {
    isLoading,
    isContactUser,
    errorMessage,
    setErrorMessage,
    userInfo,
    canModifyUser,
    verifiedUser,
    onlineUser,
    lockedUser,
    name,
    surname,
    aliasName,
    useAlias,
    gender,
    fiscalCode,
    birthDate,
    birthPlace,
    email,
    phoneNumber,
    callCode,
    userEntity,
    userGdEntity,
    gdEntityList,
    isBlacklistedEntity,
    userRoles,
    cards,
    positions,
    allowNews,
    resCountry,
    resRegion,
    resProvince,
    resCity,
    resStreet,
    resZipCode,
    resCityName,
    resRegionName,
    domCountry,
    domRegion,
    domProvince,
    domCity,
    domStreet,
    domZipCode,
    domIsRes,
    domCityName,
    domRegionName,
    setName,
    setSurname,
    setAliasName,
    setUseAlias,
    setGender,
    setFiscalCode,
    setBirthDate,
    setBirthPlace,
    setEmail,
    setPhoneNumber,
    setCallCode,
    setVerifiedUser,
    setOnlineUser,
    setLockedUser,
    setUserEntity,
    setUserGdEntity,
    setIsBlacklistedEntity,
    setUserRoles,
    setCards,
    setPositions,
    setAllowNews,
    setResCountry,
    setResRegion,
    setResProvince,
    setResCity,
    setResStreet,
    setResZipCode,
    setResCityName,
    setResRegionName,
    setDomCountry,
    setDomRegion,
    setDomProvince,
    setDomCity,
    setDomStreet,
    setDomZipCode,
    setDomIsRes,
    setDomCityName,
    setDomRegionName,
    getUserInfo,
    setUserInfo,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export default UserContext;
